/**
 * <lb-avatar> — componente universal de avatar.
 *
 * Existe para tirar o avatar do caminho crítico da página e para que exista um
 * único lugar com o markup de avatar no site inteiro (gestão e área do aluno).
 *
 * Como funciona:
 *   1. No `connectedCallback` pinta na hora o círculo com a cor da pessoa — sem
 *      imagem, sem requisição, no tamanho final (nada se move depois).
 *   2. Se o PHP já sabia a URL (`src`), a imagem entra direto.
 *   3. Se não sabia, o elemento entra numa fila. Quando aparece na viewport, a
 *      fila é despachada em UMA requisição para /labead/v1/avatars — todos os
 *      avatares visíveis de uma vez.
 *
 * Não usa Shadow DOM de propósito: o elemento precisa herdar o tema da página
 * onde está (tokens da gestão ou do ReadyLaunch), e o CSS mora em
 * `parts/components/lb-avatar.scss`.
 *
 * @package Labead
 */

/** Fila de elementos aguardando descoberta de imagem. */
const fila = new Set();

/** Handle do agendamento do despacho (para coalescer vários no mesmo lote). */
let agendado = null;

/** Cache por página: id → dado já resolvido. */
const cacheUsuarios = new Map();
const cachePosts = new Map();

/** Teto por requisição — espelha Rest_Avatars::MAX. */
const MAX_LOTE = 200;

/**
 * Observa a viewport: só entra na fila o avatar que a pessoa vai realmente ver.
 * Margem generosa para a imagem chegar antes de aparecer de fato.
 */
const observador =
	typeof IntersectionObserver !== 'undefined'
		? new IntersectionObserver(
				(entradas) => {
					entradas.forEach((e) => {
						if (!e.isIntersecting) return;
						observador.unobserve(e.target);
						enfileirar(e.target);
					});
				},
				{ rootMargin: '200px' }
		  )
		: null;

/**
 * Endpoint e nonce vêm do lb_data já impresso pelo PHP.
 *
 * @returns {{url: string, nonce: string}|null}
 */
function config() {
	const d = window.lb_data;
	if (!d || !d.rest_url) return null;
	return { url: `${d.rest_url}labead/v1/avatars`, nonce: d.rest_nonce || '' };
}

/**
 * Coloca o elemento na fila e agenda o despacho do lote.
 *
 * @param {LbAvatar} el Elemento.
 */
function enfileirar(el) {
	fila.add(el);
	if (agendado !== null) return;
	// Um quadro de atraso é suficiente para juntar a tela inteira num lote só.
	agendado = requestAnimationFrame(() => {
		agendado = null;
		despachar();
	});
}

/**
 * Manda uma requisição com todos os ids da fila e distribui o resultado.
 */
async function despachar() {
	if (!fila.size) return;

	const cfg = config();
	if (!cfg) return;

	const lote = Array.from(fila).slice(0, MAX_LOTE);
	lote.forEach((el) => fila.delete(el));

	const usuarios = new Set();
	const posts = new Set();
	let precisaRank = false;

	lote.forEach((el) => {
		const uid = el.getAttribute('uid');
		const pid = el.getAttribute('pid');
		if (uid) {
			usuarios.add(uid);
			if (el.hasAttribute('rank-auto')) precisaRank = true;
		} else if (pid) {
			posts.add(pid);
		}
	});

	const params = new URLSearchParams();
	if (usuarios.size) params.set('u', Array.from(usuarios).join(','));
	if (posts.size) params.set('p', Array.from(posts).join(','));
	if (precisaRank) params.set('rank', '1');
	// Alguém no lote já tentou e a imagem não existe: manda o servidor refazer
	// a descoberta em vez de devolver a mesma URL quebrada.
	if (lote.some((el) => el._quebrado)) params.set('force', '1');
	if (!params.toString()) return;

	try {
		const resp = await fetch(`${cfg.url}?${params.toString()}`, {
			headers: { 'X-WP-Nonce': cfg.nonce },
			credentials: 'same-origin',
		});
		const json = await resp.json();
		const dados = json && json.success ? json.data : null;
		if (!dados) return;

		Object.entries(dados.u || {}).forEach(([id, d]) => cacheUsuarios.set(id, d));
		Object.entries(dados.p || {}).forEach(([id, d]) => cachePosts.set(id, d));

		lote.forEach((el) => el.aplicarDados());
	} catch (e) {
		// Avatar nunca é crítico: se falhar, o círculo colorido continua ali.
		if (window.lbLog && window.lbLog.warn) {
			window.lbLog.warn('lb-avatar: falha ao resolver lote', e);
		}
		// Deixa o restante da fila seguir na próxima oportunidade.
		if (fila.size) enfileirar(lote[0]);
	}

	// Sobrou fila (mais de MAX_LOTE de uma vez) — próxima rodada.
	if (fila.size) {
		agendado = requestAnimationFrame(() => {
			agendado = null;
			despachar();
		});
	}
}

/**
 * Escapa texto para atributo.
 *
 * @param {*} v Valor.
 * @returns {string}
 */
function esc(v) {
	return String(v ?? '')
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

/**
 * URL "genérica" = não identifica ninguém: o PNG do tipo (`assets/img/posts/`)
 * para post, ou a silhueta padrão que vem na pasta do BuddyBoss para usuário.
 * O PNG de iniciais do BuddyBoss (`/avatars/default/`) NÃO é genérico — é o
 * segundo degrau do usuário (foto enviada > iniciais > imagem de aluno/equipe).
 *
 * @param {string} src URL.
 * @param {boolean} usuario Avatar de usuário.
 * @returns {boolean}
 */
function ehGenerica(src, usuario) {
	if (!src) return true;
	if (usuario) return src.includes('/plugins/buddyboss-platform/');
	return src.includes('/assets/img/posts/');
}

/**
 * Último degrau do usuário na gestão: imagem genérica de equipe ou de aluno
 * (o lote marca `e` para quem é equipe da escola atual). Fora da gestão o
 * lb_data não traz essas URLs e o círculo colorido continua sendo o fim.
 *
 * @param {string|null} uid Usuário.
 * @returns {string}
 */
function fallbackUsuario(uid) {
	const d = window.lb_data;
	if (!d || !d.avatar_user_fb) return '';
	const dado = uid ? cacheUsuarios.get(uid) : null;
	return dado && dado.e && d.avatar_equipe_fb ? d.avatar_equipe_fb : d.avatar_user_fb;
}

/**
 * O elemento.
 */
class LbAvatar extends HTMLElement {
	static get observedAttributes() {
		return ['src', 'cor', 'uid', 'pid', 'online', 'status-icon', 'fb', 'aspect', 'skin', 'rank'];
	}

	connectedCallback() {
		if (this._montado) return;
		this._montado = true;

		if (!this.getAttribute('size')) this.setAttribute('size', 'sm');
		this.pintar();
		this.resolver();
	}

	disconnectedCallback() {
		fila.delete(this);
		if (observador) observador.unobserve(this);
	}

	/**
	 * Decide se precisa ir buscar alguma coisa, e como.
	 *
	 * Chamado no connect e de novo quando um atributo relevante muda — o Alpine
	 * costuma preencher `:uid`/`:src` DEPOIS de inserir o elemento, então o
	 * primeiro connect pode acontecer sem identidade nenhuma.
	 */
	resolver() {
		const src = this.getAttribute('src');
		if (src) this.mostrarImagem(src);

		// Sem identidade não há o que buscar (ainda).
		if (!this.getAttribute('uid') && !this.getAttribute('pid')) return;

		// Só vai à rede quando falta alguma coisa: a imagem, ou o nível de
		// carreira que o PHP não pôde calcular sem custo (`rank-auto`).
		const faltaRank = this.hasAttribute('rank-auto') && !this.hasAttribute('rank');
		if (src && !faltaRank) return;

		// Talvez outro avatar da mesma pessoa já tenha sido resolvido nesta página.
		if (this.aplicarDados()) return;

		if (observador) {
			observador.observe(this);
		} else {
			enfileirar(this);
		}
	}

	attributeChangedCallback(nome, antigo, novo) {
		if (!this._montado || antigo === novo) return;

		if (nome === 'cor') {
			if (novo) this.style.setProperty('--lb-av-cor', novo);
			return;
		}

		if (nome === 'src') {
			if (novo) this.mostrarImagem(novo);
			return;
		}

		// O Alpine aplica `:fb`/`aspect` depois de inserir o elemento: monta a
		// camada do pôster agora (e esconde a imagem genérica que já entrou).
		if (nome === 'fb' || nome === 'aspect') {
			this.montarFb();
			const atual = this._img && this._img.getAttribute('src');
			if (atual && this.ehPoster() && ehGenerica(atual, !!this.getAttribute('uid'))) {
				this._img.classList.remove('is-on');
				this._img.removeAttribute('src');
			}
			return;
		}

		if (nome === 'uid' || nome === 'pid') {
			// Identidade nova: a imagem antiga não vale mais (reciclagem de
			// linha numa lista com Alpine).
			if (this._img) {
				this._img.classList.remove('is-on');
				this._img.removeAttribute('src');
			}
			this.removeAttribute('rank');
			this.removeAttribute('skin');
			fila.delete(this);
			if (observador) observador.unobserve(this);
			this.resolver();
			return;
		}

		if (nome === 'skin' || nome === 'rank') {
			this.pintarSkin();
			return;
		}

		// online / status-icon mudaram: só as camadas, a foto continua onde está.
		this.pintarCamadas();
	}

	/**
	 * Skin da carreira do aluno (atributo `skin` = JSON de
	 * Carreiras::skin_do_aluno). Sem skin, fica a medalha padrão do CSS.
	 * Mesmo markup de Avatar_Decorator::skin_html() no PHP.
	 */
	pintarSkin() {
		this.querySelectorAll(':scope > .lb-av-skin').forEach((n) => n.remove());
		this.removeAttribute('data-skin');

		let s = null;
		try {
			s = JSON.parse(this.getAttribute('skin') || 'null');
		} catch (e) {
			s = null;
		}
		if (!s || !s.t || !this.hasAttribute('rank')) return;

		const miolo = s.img
			? `<img src="${esc(s.img)}" alt="" />`
			: `<i data-lucide="${esc(s.i || 'medal')}"></i>`;
		// O anel vai junto em todo tipo (o "círculo" já é o anel), com a cor ou
		// o gradiente da skin — sem cor, a do nível.
		const bg = s.bg || 'transparent';
		const anel =
			s.t === 'circulo'
				? ''
				: `<span class="lb-av-skin lb-av-skin--anel" aria-hidden="true" style="background:${esc(bg === 'transparent' ? s.c || '#64748b' : bg)}"></span>`;
		this.insertAdjacentHTML(
			'beforeend',
			anel +
			`<span class="lb-av-skin lb-av-skin--${esc(s.t)}" aria-hidden="true"` +
				` style="background:${esc(s.bg || 'transparent')};--lb-skin-c:${esc(s.c || '#64748b')}">${miolo}</span>`
		);
		this.setAttribute('data-skin', s.t);

		if (!s.img && window.lucide && typeof window.lucide.createIcons === 'function') {
			window.lucide.createIcons();
		}
	}

	/**
	 * Desenha a estrutura: moldura + imagem (vazia) + pontos.
	 *
	 * A moldura e a imagem são criadas UMA vez; chamadas seguintes só refazem
	 * as camadas sobrepostas, para não perder a foto já carregada quando o
	 * status ou a presença mudam no meio da vida da linha.
	 */
	pintar() {
		const cor = this.getAttribute('cor');
		if (cor) this.style.setProperty('--lb-av-cor', cor);

		if (!this._frame) {
			const nome = this.getAttribute('nome') || '';
			this.innerHTML =
				`<span class="lb-av-frame">` +
				`<img class="lb-av-img" alt="${esc(nome)}" loading="lazy" decoding="async" />` +
				`</span>`;
			this._frame = this.querySelector('.lb-av-frame');
			this._img = this.querySelector('.lb-av-img');
		}

		this.montarFb();
		this.pintarCamadas();
		this.pintarSkin();
	}

	/**
	 * Pôster (turma/disciplina/avaliação) sem imagem própria: o mesmo pôster
	 * automático da área do aluno — cor da escola + logo — por baixo da foto,
	 * visível enquanto ela não existe. Idempotente.
	 */
	montarFb() {
		if (!this._frame || this._fb || !this.ehPoster()) return;
		const fb = window.lb_data && window.lb_data.poster_fb;
		if (!fb) return;
		this._frame.insertAdjacentHTML(
			'afterbegin',
			`<span class="lb-av-fb" style="background:${esc(fb.bg || 'var(--lb-secondary)')}">` +
				(fb.logo ? `<img class="lb-av-fb-logo" src="${esc(fb.logo)}" alt="" loading="lazy" />` : '') +
				`</span>`
		);
		this._fb = this._frame.querySelector('.lb-av-fb');
	}

	/** Pôster retrato (forma) ou só o fallback de pôster (card que já tem forma). */
	ehPoster() {
		return this.getAttribute('aspect') === 'poster' || this.getAttribute('fb') === 'poster';
	}

	/**
	 * (Re)desenha ponto de presença e ponto de status, sem tocar na imagem.
	 */
	pintarCamadas() {
		if (!this._frame) return;

		this._frame.querySelectorAll('.lb-av-dot').forEach((n) => n.remove());

		let dots = '';

		if (this.hasAttribute('online')) {
			const rot = this.getAttribute('online-label') || 'Online agora';
			dots +=
				`<span class="lb-av-dot lb-av-dot--online" role="img"` +
				` title="${esc(rot)}" aria-label="${esc(rot)}"></span>`;
		}

		const stIcone = this.getAttribute('status-icon');
		if (stIcone) {
			const stCor = this.getAttribute('status-bg') || '#64748b';
			const stRot = this.getAttribute('status-label') || '';
			const stTxt = this.getAttribute('status-color') || '';
			dots +=
				`<span class="lb-av-dot lb-av-dot--status" role="img"` +
				` style="background:${esc(stCor)}${stTxt ? `;color:${esc(stTxt)}` : ''}"` +
				` title="${esc(stRot)}" aria-label="${esc(stRot)}">` +
				`<i data-lucide="${esc(stIcone)}"></i></span>`;
			this.setAttribute('data-status', '1');
		} else {
			this.removeAttribute('data-status');
		}

		if (!dots) return;

		this._frame.insertAdjacentHTML('beforeend', dots);

		// O ícone do status é Lucide — precisa do render da lib para virar SVG.
		if (stIcone && window.lucide && typeof window.lucide.createIcons === 'function') {
			window.lucide.createIcons();
		}
	}

	/**
	 * Coloca a imagem, revelando só quando ela realmente carregou (evita o
	 * quadrado quebrado quando o arquivo sumiu do disco).
	 *
	 * @param {string} src URL.
	 */
	mostrarImagem(src) {
		if (!this._img || !src) return;

		const usuario = !!this.getAttribute('uid');
		if (ehGenerica(src, usuario)) {
			// Pôster: a imagem genérica do tipo não entra — fica o pôster da escola.
			if (!usuario && this.ehPoster()) return;
			// Usuário: silhueta do BuddyBoss vira a imagem de aluno/equipe da gestão.
			const fbUser = usuario && fallbackUsuario(this.getAttribute('uid'));
			if (fbUser) src = fbUser;
		}

		this._img.onload = () => {
			this._img.classList.add('is-on');
			this._quebrado = false;
		};

		this._img.onerror = () => {
			this._img.classList.remove('is-on');

			// A URL guardada aponta para um arquivo que não existe mais (o
			// BuddyBoss regera o PNG de iniciais com outro nome, e um clone de
			// banco pode trazer URLs sem os arquivos correspondentes). Pede ao
			// servidor para refazer a descoberta — uma vez só, para uma imagem
			// realmente ausente não virar laço.
			if (this._quebrado || (!this.getAttribute('uid') && !this.getAttribute('pid'))) return;
			this._quebrado = true;
			cacheUsuarios.delete(this.getAttribute('uid'));
			cachePosts.delete(this.getAttribute('pid'));
			enfileirar(this);
		};

		this._img.src = src;
	}

	/**
	 * Aplica o dado já conhecido (do cache da página ou do lote recém-chegado).
	 *
	 * @returns {boolean} true quando havia dado para aplicar.
	 */
	aplicarDados() {
		const uid = this.getAttribute('uid');
		const pid = this.getAttribute('pid');
		const d = uid ? cacheUsuarios.get(uid) : pid ? cachePosts.get(pid) : null;
		if (!d) return false;

		if (d.c) this.style.setProperty('--lb-av-cor', d.c);
		if (typeof d.n === 'number' && this.hasAttribute('rank-auto')) {
			if (d.n > 0) {
				this.setAttribute('rank', String(d.n));
			} else {
				this.removeAttribute('rank');
			}
			if (d.k && d.n > 0) {
				this.setAttribute('skin', JSON.stringify(d.k));
			} else {
				this.removeAttribute('skin');
			}
		}
		// Depois de um erro, só vale reaplicar se a URL mudou — repetir a mesma
		// não conserta nada e voltaria a falhar.
		if (d.s && (!this._quebrado || d.s !== this._img?.getAttribute('src'))) {
			this.mostrarImagem(d.s);
		} else if (!d.s && uid && fallbackUsuario(uid)) {
			// Sem foto nem PNG de iniciais: a genérica de aluno/equipe da gestão.
			this.mostrarImagem(fallbackUsuario(uid));
		}

		return true;
	}
}

if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('lb-avatar')) {
	window.customElements.define('lb-avatar', LbAvatar);
}

export default LbAvatar;
